import styles from "./MusicSearchBar.module.scss";
import { useMusicSearch } from "../../hooks/useMusicSearch";
import { MusicTrackItem } from "../MusicTrackItem";
import { classNames } from "primereact/utils";
import { useRef, useEffect } from "react";

interface IMusicSearchBarProps {
  onTrackSelect: (trackId: string) => void;
  audioPlayer: {
    play: (trackId: string, previewUrl: string) => void;
    stop: () => void;
    isTrackPlaying: (trackId: string) => boolean;
    currentTrackId: string | null;
    isPlaying: boolean;
  };
}

export const MusicSearchBar = ({ onTrackSelect, audioPlayer }: IMusicSearchBarProps) => {
  const {
    searchQuery,
    tracks,
    isLoading,
    handleSearch,
    clearSearch,
    hasResults,
    showResults,
  } = useMusicSearch();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousSearchQuery = useRef<string>("");

  // Stop audio when search query changes
  useEffect(() => {
    if (previousSearchQuery.current !== "" && searchQuery !== previousSearchQuery.current) {
      audioPlayer.stop();
    }
    previousSearchQuery.current = searchQuery;
  }, [searchQuery, audioPlayer]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        // Don't clear search, just let dropdown hide naturally
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTrackSelect = (trackId: string) => {
    audioPlayer.stop();
    onTrackSelect(trackId);
    clearSearch();
  };

  const handleClearSearch = () => {
    audioPlayer.stop();
    clearSearch();
  };

  return (
    <div className={styles.searchContainer} ref={dropdownRef}>
      {/* Search Input */}
      <div className={styles.searchInputWrapper}>
        <i className="pi pi-search" />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Търсене на песни..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
        {searchQuery && (
          <button
            className={styles.clearButton}
            onClick={handleClearSearch}
            title="Clear search"
          >
            <i className="pi pi-times" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {showResults && (
        <div
          className={classNames(styles.dropdown, {
            [styles.visible]: showResults,
          })}
        >
          {isLoading && (
            <div className={styles.loadingState}>
              <i className="pi pi-spin pi-spinner" />
              <span>Търсене...</span>
            </div>
          )}

          {!isLoading && !hasResults && (
            <div className={styles.emptyState}>
              <i className="pi pi-search" />
              <span>Няма намерени резултати</span>
            </div>
          )}

          {!isLoading && hasResults && (
            <div className={styles.resultsList}>
              {tracks.map((track) => (
                <MusicTrackItem
                  key={track.id}
                  track={track}
                  variant="search"
                  onSelect={() => handleTrackSelect(track.id)}
                  isPlaying={audioPlayer.isTrackPlaying(track.id)}
                  onPlayToggle={
                    track.previewUrl
                      ? () => audioPlayer.play(track.id, track.previewUrl!)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
